import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useLanguage } from '@shared/contexts/LanguageContext';
import { getLightingPostList, toggleLightingPostLike } from '@features/lamp-lighting/api/lampService';
import { ROUTES } from '@app/router/routes';
import { BlockRenderer } from '@shared/BlockRenderer';
import PageHeader from '@shared/layouts/PageHeader';
import BottomNav from '@shared/layouts/BottomNav';
import { getUserInfo } from '@shared/api/client';
import './BranchReportDetailPage.css';

const BranchReportDetailPage = () => {
    const { branchId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { t, currentLanguage } = useLanguage();
    const [postData, setPostData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [countdown, setCountdown] = useState(3);
    const [likeCount, setLikeCount] = useState(0);
    const [isLiked, setIsLiked] = useState(false);
    const [likeLoading, setLikeLoading] = useState(false);
    const monasteryName = location.state?.monasteryName || '';
    const [user, setUser] = useState(() => getUserInfo());

    useEffect(() => {
        const onAuthChange = () => setUser(getUserInfo());
        window.addEventListener('auth-change', onAuthChange);
        return () => window.removeEventListener('auth-change', onAuthChange);
    }, []);

    // 套用字體（此頁面不在 PageLayoutWrapper 內，手動套用）
    useEffect(() => {
        const saved = localStorage.getItem('preferred-font');
        const fontClass = saved === 'primary' ? 'font-round' : 'font-serif';
        document.body.classList.remove('font-round', 'font-serif');
        document.body.classList.add(fontClass);
    }, []);

    // 查無資料時倒數導向
    useEffect(() => {
        if (!notFound) return;
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    navigate(ROUTES.WISDOM_LAMP, { replace: true });
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [notFound]);

    useEffect(() => {
        if (!branchId) {
            setNotFound(true);
            setLoading(false);
            return;
        }
        setLoading(true);
        getLightingPostList({ monastery_id: parseInt(branchId), page: 1, per_page: 10 })
            .then((res) => {
                if (res?.items && res.items.length > 0) {
                    const post = res.items[0];
                    setPostData(post);
                    setLikeCount(post.like_count ?? 0);
                    setIsLiked(post.has_liked ?? post.is_liked ?? false);
                } else {
                    setNotFound(true);
                }
            })
            .catch((err) => {
                console.error('Failed to fetch lighting post:', err);
                setNotFound(true);
            })
            .finally(() => setLoading(false));
    }, [branchId, currentLanguage]);

    const handleLike = async () => {
        if (likeLoading || isLiked || !postData?.post_id) return;
        setLikeLoading(true);
        try {
            const res = await toggleLightingPostLike(postData.post_id, true);
            setIsLiked(true);
            setLikeCount(res.like_count ?? likeCount + 1);
        } catch (err) {
            console.error('Failed to like:', err);
        } finally {
            setLikeLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--color-background)]">
                <div className="text-lg text-[var(--color-text-secondary)]">{t('branch-reports.loading')}</div>
            </div>
        );
    }

    if (notFound) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--color-background)]">
                <div className="text-center px-8">
                    <p className="text-6xl font-bold text-[var(--color-gold)] mb-4">404</p>
                    <h1 className="text-2xl font-bold text-[var(--color-text-main)] mb-3">{t('branch-reports.notFoundTitle')}</h1>
                    <p className="text-base text-[var(--color-text-sub)]">
                        {t('branch-reports.notFoundRedirect').replace('{{count}}', countdown)}
                    </p>
                </div>
            </div>
        );
    }

    if (!postData) {
        return null;
    }

    return (
        <div className="min-h-screen bg-white relative">
            <PageHeader user={user} showHome />

            {/* Back Button (Fixed, stays on screen while scrolling) */}
            <div className="fixed top-[80px] md:top-[96px] left-4 md:left-8 z-10">
                <button
                    onClick={() => navigate(-1)}
                    className="flex justify-center items-center w-10 h-10 rounded-full bg-white/60 shadow-[0_2px_8px_rgba(0,0,0,0.1)] backdrop-blur text-[var(--color-primary)] hover:bg-white transition-colors cursor-pointer"
                    aria-label={t('branch-reports.backAria')}
                >
                    <ChevronLeft size={24} className="mr-0.5" />
                </button>
            </div>

            {/* Fixed Like Button */}
            <div className="fixed right-4 md:right-6 bottom-24 z-50">
                <button
                    onClick={handleLike}
                    disabled={likeLoading || isLiked}
                    className={`flex flex-col items-center gap-1 w-14 h-14 rounded-full border-2 shadow-xl transition-all duration-200 select-none
                        ${isLiked
                            ? 'bg-[var(--color-primary)] border-[var(--color-primary)] text-white cursor-default'
                            : 'bg-white/90 border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white cursor-pointer'
                        }
                        ${likeLoading ? 'opacity-60 cursor-not-allowed' : ''}
                    `}
                    aria-label={t('branch-reports.likeAria')}
                >
                    <span
                        className="material-symbols-outlined text-xl leading-none mt-2"
                        style={{ fontVariationSettings: isLiked ? "'FILL' 1" : "'FILL' 0" }}
                    >
                        favorite
                    </span>
                    <span className="text-xs font-bold leading-none mb-1">{likeCount}</span>
                </button>
            </div>

            {/* Block Content */}
            {postData.template_detail?.length > 0 ? (
                <section className="branch-report-content-section pt-[70px] md:pt-[86px] pb-10">
                    <BlockRenderer
                        blocks={postData.template_detail}
                        templateId={postData.template_id}
                    />
                </section>
            ) : (
                <section className="py-20 px-6 bg-[var(--color-background)]">
                    <div className="max-w-5xl mx-auto text-center">
                        <div className="text-[var(--color-text-sub)] mb-4">
                            <span className="material-symbols-outlined text-6xl">article</span>
                        </div>
                        <p className="text-lg text-[var(--color-text-sub)]">{t('branch-reports.noContent')}</p>
                    </div>
                </section>
            )}
            <BottomNav className="md:hidden" />
        </div>
    );
};

export default BranchReportDetailPage;
